import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { createRecording, identify } from "@/api/client";
import type { IdentifyInput } from "@/api/types";
import { recallObserver, rememberObserver } from "@/lib/observer";
import { ACCEPT, checkUpload, type UploadKind } from "@/lib/upload";
import { count, to, useStats } from "@/site/parts";

/** Navigation state: the object URL of the file just sent, shown until the server's copy loads. */
export interface UploadNavState {
  preview?: string;
}

/**
 * Navigation state of a recording: the object URL of the uploaded video. It lives only as long
 * as this browser session holds the file; the server keeps the sampled frames, not the video.
 */
export interface RecordingNavState {
  video?: string;
}

/** Send the file the way its kind is measured; returns where its result will appear. */
async function send(kind: UploadKind, input: IdentifyInput, objectUrl: string) {
  if (kind === "video") {
    const recording = await createRecording(input);
    const state: RecordingNavState = { video: objectUrl };
    return { path: to.recording(recording.id), state };
  }
  const query = await identify(input);
  const state: UploadNavState = { preview: objectUrl };
  return { path: to.identify(query.id), state };
}

/** The whole window is a drop target while the stage is mounted. */
function useWindowDrop(onFile: (file: File) => void): boolean {
  const [dragging, setDragging] = useState(false);
  const ref = useRef(onFile);
  useLayoutEffect(() => {
    ref.current = onFile;
  });
  useEffect(() => {
    let depth = 0;
    const hasFiles = (e: DragEvent) => e.dataTransfer?.types.includes("Files") ?? false;
    const onEnter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth += 1;
      setDragging(true);
    };
    const onLeave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth = Math.max(0, depth - 1);
      if (depth === 0) setDragging(false);
    };
    const onOver = (e: DragEvent) => {
      if (hasFiles(e)) e.preventDefault();
    };
    const onDrop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth = 0;
      setDragging(false);
      const file = e.dataTransfer?.files[0];
      if (file) ref.current(file);
    };
    window.addEventListener("dragenter", onEnter);
    window.addEventListener("dragleave", onLeave);
    window.addEventListener("dragover", onOver);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onEnter);
      window.removeEventListener("dragleave", onLeave);
      window.removeEventListener("dragover", onOver);
      window.removeEventListener("drop", onDrop);
    };
  }, []);
  return dragging;
}

function Reticle() {
  return (
    <svg className="stage-reticle" viewBox="0 0 64 64" aria-hidden="true">
      <circle cx="32" cy="32" r="22" fill="none" stroke="currentColor" strokeWidth="1" />
      <circle cx="32" cy="32" r="3" fill="var(--signal)" />
      <path
        d="M32 0v18M32 46v18M0 32h18M46 32h18"
        stroke="currentColor"
        strokeWidth="1"
        fill="none"
      />
    </svg>
  );
}

export function UploadStage() {
  const navigate = useNavigate();
  const stats = useStats();
  const ids = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ url: string; kind: UploadKind } | null>(null);
  const [observer, setObserver] = useState(recallObserver);
  const [date, setDate] = useState("");
  const [locality, setLocality] = useState("");
  const [today] = useState(() => new Date().toISOString().slice(0, 10));

  async function submit(file: File) {
    if (busy) return;
    const checked = checkUpload(file);
    if ("problem" in checked) {
      setError(checked.problem);
      return;
    }
    setError(null);
    setBusy(true);
    const objectUrl = URL.createObjectURL(file);
    setPreview({ url: objectUrl, kind: checked.kind });
    if (observer.trim()) rememberObserver(observer.trim());
    try {
      const input = {
        file,
        observer: observer.trim() || undefined,
        date: date || undefined,
        locality: locality.trim() || undefined,
      };
      const { path, state } = await send(checked.kind, input, objectUrl);
      navigate(path, { state });
    } catch (err) {
      setBusy(false);
      setPreview(null);
      URL.revokeObjectURL(objectUrl);
      setError(`Upload failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  const dragging = useWindowDrop((file) => void submit(file));

  return (
    <div>
      <label
        htmlFor={`${ids}-file`}
        className="stage"
        data-drag={dragging}
        aria-busy={busy}
        style={busy ? { pointerEvents: "none" } : undefined}
      >
        {preview?.kind === "photo" && <img src={preview.url} alt="" className="stage-preview" />}
        {preview?.kind === "video" && (
          <video src={preview.url} className="stage-preview" muted playsInline aria-hidden />
        )}
        {busy && <div className="scan" aria-hidden="true" />}
        <span className="stage-mark tl" />
        <span className="stage-mark tr" />
        <span className="stage-mark bl" />
        <span className="stage-mark br" />
        <span className="stage-corner tl label">Input · photograph or video</span>
        <span className="stage-corner tr label">
          Compared against {count(stats?.sharks)} individuals
        </span>
        <span className="stage-corner bl label">JPEG · PNG · HEIC · MP4 · MOV · WebM</span>
        <span className="stage-corner br label">Photo ≤ 20 MB · video ≤ 200 MB</span>
        <span className="stage-body">
          <Reticle />
          <span className="display-2" style={{ fontSize: "clamp(30px, 3.4vw, 52px)" }}>
            {busy ? "Transmitting…" : "Drop a whale shark photograph or video"}
          </span>
          <span className="muted" style={{ maxWidth: "32em" }}>
            {busy
              ? `Sending the ${preview?.kind === "video" ? "video" : "image"} to the instrument.`
              : "Side-on, showing the flank behind the gills. A video is read one frame every " +
                "two seconds."}
          </span>
          {!busy && (
            <span className="btn" aria-hidden="true" style={{ marginTop: 6 }}>
              Choose photo or video <span className="btn-arrow">↑</span>
            </span>
          )}
        </span>
        <input
          id={`${ids}-file`}
          type="file"
          accept={ACCEPT}
          className="sr-only"
          disabled={busy}
          aria-describedby={`${ids}-hint`}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void submit(file);
            e.target.value = "";
          }}
        />
      </label>
      <p id={`${ids}-hint`} className="sr-only">
        Choose or drop a photograph of a whale shark&rsquo;s flank or back, up to 20 megabytes, or a
        video of one, up to 200 megabytes.
      </p>
      {error && (
        <p role="alert" className="alert" style={{ marginTop: 16 }}>
          {error}
        </p>
      )}
      <div className="in-grid meta-row" role="group" aria-labelledby={`${ids}-meta`}>
        <p id={`${ids}-meta`} className="label" style={{ paddingTop: 22 }}>
          Sighting metadata · optional
        </p>
        <label className="field">
          <span className="label">Observer</span>
          <input
            className="input"
            value={observer}
            autoComplete="name"
            placeholder="Ana Ruiz"
            onChange={(e) => setObserver(e.target.value)}
          />
        </label>
        <label className="field">
          <span className="label">Date of encounter</span>
          <input
            className="input"
            type="date"
            max={today}
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </label>
        <label className="field">
          <span className="label">Locality</span>
          <input
            className="input"
            value={locality}
            placeholder="Ningaloo Reef"
            onChange={(e) => setLocality(e.target.value)}
          />
        </label>
      </div>
    </div>
  );
}
